import React, { useCallback, useEffect, useState } from 'react';
import Button from '../../components/common/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import RelativeTime from '../../components/common/RelativeTime';
import { Competition } from '../../types/interfaces';
import { AdminSubmission, DeleteSubmissionResult, errorMessage, fetchAdminSubmissions, ownerLabel } from '../../services/manageApi';
import ResetJudgingDialog from './ResetJudgingDialog';
import DeleteSubmissionDialog from './DeleteSubmissionDialog';

interface Props {
  competition: Competition;
  canReset: boolean;
  canDelete: boolean;
  refreshKey: number;
  onJudgingChanged: () => void;
}

const DangerZone: React.FC<Props> = ({ competition, canReset, canDelete, refreshKey, onJudgingChanged }) => {
  const [resetOpen, setResetOpen] = useState(false);
  const [submissions, setSubmissions] = useState<AdminSubmission[] | null>(null);
  const [listError, setListError] = useState('');
  const [toDelete, setToDelete] = useState<AdminSubmission | null>(null);
  const [warning, setWarning] = useState<{ owner: string; files: string[] } | null>(null);

  const loadSubmissions = useCallback(async () => {
    setListError('');
    try {
      setSubmissions(await fetchAdminSubmissions(competition.id));
    } catch (err) {
      setSubmissions(null);
      setListError(errorMessage(err, "Couldn't load submissions"));
    }
  }, [competition.id]);

  useEffect(() => {
    if (canDelete) loadSubmissions();
  }, [canDelete, loadSubmissions, refreshKey]);

  // The files warning outlives the reload that follows a delete; it goes when
  // the next delete starts.
  const openDelete = (submission: AdminSubmission) => {
    setWarning(null);
    setToDelete(submission);
  };

  const handleDeleted = (result: DeleteSubmissionResult) => {
    const deletedId = toDelete?.submission_id;
    const owner = toDelete ? ownerLabel(toDelete) : '';
    setToDelete(null);
    setSubmissions((prev) => (prev ? prev.filter((s) => s.submission_id !== deletedId) : prev));
    setWarning(result.files_failed.length > 0 ? { owner, files: result.files_failed } : null);
    onJudgingChanged();
  };

  return (
    <section className="manage-section danger-zone" data-tour="manage-danger-zone">
      <h3>Danger zone — platform admin</h3>
      <p className="manage-field__help">These actions are permanent. Use them to clean up test runs before the competition.</p>

      {canReset && (
        <div className="danger-zone__block">
          <h4>Reset judging</h4>
          <p className="manage-field__help">Deletes all votes and evaluations and resets judges, ELO and rankings. Submissions are kept.</p>
          <Button variant="danger" size="sm" onClick={() => setResetOpen(true)}>Reset judging…</Button>
        </div>
      )}

      {canDelete && (
        <div className="danger-zone__block">
          <h4>Submissions</h4>
          {warning && (
            <div className="danger-zone__warning" role="status">
              Files not removed for {warning.owner}: the submission was deleted, but these files could not be
              removed from storage:
              <ul>{warning.files.map((key) => <li key={key}>{key}</li>)}</ul>
            </div>
          )}
          {listError ? (
            <div className="manage-error" role="alert">
              {listError} <Button variant="ghost" size="sm" onClick={loadSubmissions}>Retry</Button>
            </div>
          ) : !submissions ? (
            <LoadingSpinner size="sm" text="Loading submissions..." />
          ) : submissions.length === 0 ? (
            <p className="manage-field__help">No submissions.</p>
          ) : (
            <table className="danger-zone__table">
              <thead>
                <tr>
                  <th>Owner</th>
                  <th>Version</th>
                  <th>Submitted</th>
                  <th>Votes</th>
                  <th>Manual</th>
                  <th>Rubric</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {submissions.map((s) => (
                  <tr key={s.submission_id}>
                    <td>{ownerLabel(s)}</td>
                    <td>{s.version ?? '—'}</td>
                    <td><RelativeTime value={s.submitted_at} fallback="—" /></td>
                    <td>{s.votes}</td>
                    <td>{s.manual_scores}</td>
                    <td>{s.rubric_evaluations}</td>
                    <td>
                      <Button
                        variant="outline"
                        size="sm"
                        className="danger-zone__delete"
                        ariaLabel={`Delete submission by ${ownerLabel(s)}`}
                        onClick={() => openDelete(s)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {resetOpen && (
        <ResetJudgingDialog
          competitionId={competition.id}
          title={competition.title}
          onClose={() => setResetOpen(false)}
          onDone={onJudgingChanged}
        />
      )}
      {toDelete && (
        <DeleteSubmissionDialog
          competitionId={competition.id}
          submission={toDelete}
          onClose={() => setToDelete(null)}
          onDeleted={handleDeleted}
        />
      )}
    </section>
  );
};

export default DangerZone;
